import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { loginDiwali, startDiwali, submitDiwaliStage } from "./diwaliSlice";
import DiwaliHeader from "./DiwaliHeader";
import "./diwali.css";

const ASSET_BASE = "/diwaliEscapeRoom";
const PLANS_URL = "https://extramileplay.com/plans";
// Closed-diya audio clue served from S3 instead of the local public/ bundle —
// same direct-filename convention as the intro videos in DiwaliIndex.jsx (no
// "images/" subpath there, unlike the local copy).
const S3_AUDIO_URL = `${process.env.REACT_APP_S3_PATH}Audioconversation.mp3`;

// mobile_main.jpg has no id'd hotspot elements the way EscapeRoom.svg does
// (it's a flat portrait recomposition, not the same interactive artwork), so
// each stage's tap target is positioned by eye over its nearest visual analog
// in that image, confirmed against a reference screenshot: the patterned rug
// (Rangoli), the wooden cabinet on the right (closeddiya — its diya art is
// baked directly into mobile_main.jpg, composited there with sharp from
// closeddiya.svg), and the sweets on the round table (laddoo). Frame/Hidden
// Door share one spot — see PORTRAIT_FRAME_BOX below, they're handled
// differently since Frame has to visibly fall away to reveal the door.
const PORTRAIT_HOTSPOTS = {
  rangoli: { left: "0%", top: "56%", width: "30%", height: "17%" },
  closeddiya: { left: "82%", top: "35%", width: "18%", height: "8%" },
  laddoo: { left: "62%", top: "60%", width: "37%", height: "13%" },
};

// The framed picture's exact box in mobile_main.jpg (also where
// frame_overlay.png — a crop of that same picture, pulled out with sharp —
// is positioned). Hidden Door's art is baked permanently into the
// background at this same spot underneath the frame (also composited with
// sharp, from hidden_door.png), so once frame_overlay.png animates away for
// good the door is just already there — same trick as closeddiya, but here
// the reveal has to happen live instead of always being visible.
const PORTRAIT_FRAME_BOX = { left: "38%", top: "23%", width: "30%", height: "13%" };

// Landscape/desktop scene: background-black.jpg is the same 1920x1080 artwork
// EscapeRoom.svg was drawn in, so every box below is that scene's own
// coordinates (measured from the SVG's #Rangoli / #closeddiya /
// #Motichoor_Laddoo_Clickable / #locked_Door elements) turned into
// percentages of the stage. Boxes are [x, y, width, height] in scene units.
const SCENE_W = 1920;
const SCENE_H = 1080;
const sceneBox = ([x, y, w, h]) => ({
  left: `${(x / SCENE_W) * 100}%`,
  top: `${(y / SCENE_H) * 100}%`,
  width: `${(w / SCENE_W) * 100}%`,
  height: `${(h / SCENE_H) * 100}%`,
});
const LANDSCAPE_HOTSPOTS = {
  rangoli: sceneBox([22.9, 716.7, 335.8, 80.4]),
  closeddiya: sceneBox([1410, 492, 78, 48]),
  laddoo: sceneBox([1140, 728, 92, 68]),
};
// The framed picture. Its hidden door (the dark panel + hidden_door.png) sits
// underneath at the same spot, exactly where the SVG drew them.
const FRAME_RECT = [874.6, 237.4, 170.8, 227.2];
const LANDSCAPE_FRAME_BOX = sceneBox(FRAME_RECT);
const LANDSCAPE_DOOR_IMAGE_PX = [891.22, 240.14, 206 * 0.67];
// The frame layer shows just that rectangle of background-black.jpg: the
// image is scaled up to the scene size and shifted so the rectangle lines up.
const LANDSCAPE_FRAME_IMAGE = {
  width: `${(SCENE_W / FRAME_RECT[2]) * 100}%`,
  left: `${(-FRAME_RECT[0] / FRAME_RECT[2]) * 100}%`,
  top: `${(-FRAME_RECT[1] / FRAME_RECT[3]) * 100}%`,
};

// glowstone-old/game.php equivalent: the four-hotspot escape room built on
// top of the background-black.jpg scene (Rangoli puzzle -> closed diya/audio clue
// -> Motichoor Laddoo -> Frame), each stage persisted via /submitDiwaliStage
// (rounds 1-4). The Hidden Door hands off to DiwaliPoker.jsx (round 5),
// matching legacy's `window.location.href = "Poker/index.php"`.
//
// Mirrors the legacy jQuery structure: a single re-entrant "advance" step
// runs every time the shared Next-button popup is dismissed, inspecting
// s1..s4 to decide which hotspot to arm next (same if/else-if chain as
// game.php's `$(".next").click(...)`).
export default function DiwaliGame() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, id: gameId, s1, s2, s3, s4, completed, points: savedPoints } = useSelector(
    (state) => state.diwali
  );
  const sessionId = user?.sessionId;
  const startDiwaliAttemptedRef = useRef(false);

  // A direct visit or reload of this page has no Redux state (it only ever
  // lives in memory) — restore the session from sessionStorage and resume
  // the stat row, otherwise every submitDiwaliStage call below rejects with
  // "Game not started" (state.diwali.id is required and stays null).
  useEffect(() => {
    if (!user) dispatch(loginDiwali());
  }, [user, dispatch]);

  // Gated by a ref, not `status !== "loading"` — that condition is also true
  // once a rejected attempt settles into status "failed", and with `status`
  // in the dependency array that retried on every single failure, forever,
  // hammering the backend once a second for as long as it kept failing.
  // One attempt per mount; submitDiwaliStage has its own separate,
  // deliberate one-shot retry at the point of actually saving (see
  // diwaliSlice.js).
  useEffect(() => {
    if (user && !gameId && !startDiwaliAttemptedRef.current) {
      startDiwaliAttemptedRef.current = true;
      dispatch(startDiwali());
    }
  }, [user, gameId, dispatch]);

  const audioRef = useRef(null);
  const hiddenDoorClickedRef = useRef(false);
  const savingRef = useRef(false);
  const pointsRef = useRef(savedPoints || 2);
  const flagsRef = useRef({ s1: Boolean(s1), s2: Boolean(s2), s3: Boolean(s3), s4: Boolean(s4) });

  const [sceneReady, setSceneReady] = useState(false);
  const [portraitBgReady, setPortraitBgReady] = useState(false);
  const [isPortrait, setIsPortrait] = useState(
    () => window.innerHeight > window.innerWidth
  );
  // Gates the very first popup — without this it renders immediately on
  // mount (before the background image has loaded), flashing over a
  // blank/plain background for a moment.
  const backgroundReady = isPortrait ? portraitBgReady : sceneReady;

  const [popup, setPopup] = useState({ open: true, image: "1-8", mode: "next" });
  // which hotspot currently has an active click handler
  const [armed, setArmed] = useState(null);

  const [rangoliOpen, setRangoliOpen] = useState(false);
  const rot1CountRef = useRef(6); // legacy img1rotatecount starting offset
  const rot2CountRef = useRef(1); // legacy img2rotatecount starting offset
  // Legacy #child1/#child2 wrapper base rotation (-30deg / -80deg) is what
  // scrambles the pieces initially; each click then adds +10deg on top.
  const RANGOLI_BASE_1 = -30;
  const RANGOLI_BASE_2 = -80;
  const [rot1Extra, setRot1Extra] = useState(0);
  const [rot2Extra, setRot2Extra] = useState(0);

  const [audioStageOpen, setAudioStageOpen] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);

  const [frameGone, setFrameGone] = useState(false);
  const [frameAnimating, setFrameAnimating] = useState(false);

  // Already completed (a returning user, not someone who just finished) —
  // bounce out to the platform's game page instead of /diwali/thankyou,
  // which is reserved for the moment right after actually finishing (see
  // DiwaliPoker.jsx's window.send). Same destination DiwaliHeader's Back
  // button uses.
  useEffect(() => {
    if (completed) {
      window.location.href = user?.gameId
        ? `${process.env.REACT_APP_BASE_URL}/game-detail/${user.gameId}`
        : process.env.REACT_APP_BASE_URL;
    }
  }, [completed, user]);

  useEffect(() => {
    const check = () => setIsPortrait(window.innerHeight > window.innerWidth);
    check();
    // Event-driven instead of a 1 s poll: resize covers window/URL-bar changes
    // and orientationchange covers rotation on devices that don't fire resize.
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  // Resolves true only once the backend has persisted the stage, so callers
  // can hold the UI on the current puzzle when the save fails. Points are
  // committed to pointsRef on success only, so a retry doesn't double-count.
  const submitStage = useCallback(
    (round, pointsDelta = 0) => {
      const nextPoints = pointsRef.current + pointsDelta;
      return dispatch(submitDiwaliStage({ points: nextPoints, round }))
        .unwrap()
        .then(() => {
          pointsRef.current = nextPoints;
          return true;
        })
        .catch((err) => {
          Swal.fire(
            String(err || "Failed to save progress. Please try again."),
            "",
            "error"
          );
          return false;
        });
    },
    [dispatch]
  );

  // Saved progress arrives asynchronously on a cold reload, after the refs
  // above were seeded from still-empty Redux state. Merge it in (never
  // lowering what this session has already earned) and re-arm the right stage.
  useEffect(() => {
    const f = flagsRef.current;
    const saved = { s1: Boolean(s1), s2: Boolean(s2), s3: Boolean(s3), s4: Boolean(s4) };
    let changed = false;
    Object.keys(saved).forEach((k) => {
      if (saved[k] && !f[k]) {
        f[k] = true;
        changed = true;
      }
    });
    if (savedPoints && savedPoints > pointsRef.current) {
      pointsRef.current = savedPoints;
    }
    if (changed && sceneReady) advance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s1, s2, s3, s4, savedPoints]);

  // Single re-entrant step, run whenever a "next" popup is dismissed (or on
  // first mount, to arm whatever stage is still pending on a resumed game).
  const advance = useCallback(() => {
    const f = flagsRef.current;
    if (!f.s1) {
      setArmed("rangoli");
      return;
    }
    if (!f.s2) {
      if (sessionId === "demobypass") {
        Swal.fire(
          "Subscribe to any PLAN to play with your peers.",
          "",
          "success"
        ).then(() => {
          window.location.href = PLANS_URL;
        });
        return;
      }
      setArmed("closeddiya");
      return;
    }
    if (!f.s3) {
      setArmed("laddoo");
      return;
    }
    if (!f.s4) {
      setTimeout(() => {
        setPopup({ open: true, image: "FramePopup", mode: "cross" });
        setArmed("frame");
      }, 800);
      return;
    }
    setArmed("hiddenDoor");
  }, [sessionId]);

  const closeNextPopup = useCallback(() => {
    setPopup((p) => ({ ...p, open: false }));
    advance();
  }, [advance]);

  const closeCrossPopup = () => setPopup((p) => ({ ...p, open: false }));

  // Stage-completion logic, shared between the landscape scene's invisible
  // hotspots (LANDSCAPE_HOTSPOTS) and the portrait overlay's tap zones
  // (PORTRAIT_HOTSPOTS) — same outcome either way, just a different box.
  const onRangoliHit = useCallback(() => {
    setRangoliOpen(true);
  }, []);
  const onClosedDiyaHit = useCallback(() => {
    pointsRef.current += 1;
    setAudioStageOpen(true);
  }, []);
  const onLaddooHit = useCallback(() => {
    if (savingRef.current) return;
    savingRef.current = true;
    submitStage(3, 1).then((ok) => {
      savingRef.current = false;
      if (!ok) return; // stay armed so the player can click again
      flagsRef.current.s3 = true;
      setPopup({ open: true, image: "4-8", mode: "next" });
    });
  }, [submitStage]);
  const onFrameHit = useCallback(() => {
    if (frameAnimating || frameGone) return;
    setFrameAnimating(true);
    // legacy resends the unchanged points total here
    submitStage(4, 0).then((ok) => {
      if (!ok) {
        setFrameAnimating(false);
        return;
      }
      setTimeout(() => {
        setFrameGone(true);
        flagsRef.current.s4 = true;
        setArmed("hiddenDoor");
      }, 1100);
    });
  }, [frameAnimating, frameGone, submitStage]);
  const onHiddenDoorHit = useCallback(() => {
    if (hiddenDoorClickedRef.current) return;
    hiddenDoorClickedRef.current = true;
    pointsRef.current += 2;
    navigate("/diwali/game/poker");
  }, [navigate]);

  const hitHandlers = {
    rangoli: onRangoliHit,
    closeddiya: onClosedDiyaHit,
    laddoo: onLaddooHit,
    frame: onFrameHit,
    hiddenDoor: onHiddenDoorHit,
  };

  // Arm the correct hotspot on mount (handles both a fresh game and a resume).
  useEffect(() => {
    if (sceneReady) advance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneReady]);

  const handleRangoliRotate = (which) => {
    if (which === 1) {
      rot1CountRef.current += 1;
      setRot1Extra((d) => d + 10);
    } else {
      rot2CountRef.current += 1;
      setRot2Extra((d) => d + 10);
    }
  };

  const handleRangoliSubmit = () => {
    const aligned = rot1CountRef.current % 9 === 0 && rot2CountRef.current % 9 === 0;
    if (!aligned) {
      Swal.fire(
        "The rangoli is misaligned. Please adjust it properly before continuing.",
        "",
        ""
      );
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;
    submitStage(1, 1).then((ok) => {
      savingRef.current = false;
      if (!ok) return; // keep the puzzle open so the player can retry
      setRangoliOpen(false);
      flagsRef.current.s1 = true;
      setPopup({ open: true, image: "2-8", mode: "next" });
    });
  };

  const handleAudioButtonClick = () => {
    setAudioPlaying(true);
    const p = audioRef.current?.play();
    // A rejected play() (blocked/failed media) must not leave Play disabled.
    if (p && p.catch) p.catch(() => setAudioPlaying(false));
  };

  const handleAudioEnded = () => {
    if (savingRef.current) return;
    savingRef.current = true;
    // points already incremented on closeddiya click
    submitStage(2, 0).then((ok) => {
      savingRef.current = false;
      if (!ok) return setAudioPlaying(false);
      setAudioStageOpen(false);
      flagsRef.current.s2 = true;
      setPopup({ open: true, image: "3-8", mode: "next" });
    });
  };

  // frameAnimating/frameGone only matter while "frame" itself is armed (they
  // gate re-clicking mid-animation) — they stay true for the rest of the
  // game afterward, so they must NOT gate later stages too. Gating every
  // stage on them (as an earlier version of this did) meant hiddenDoor could
  // never render once frameGone flipped true, softlocking portrait play.
  const canTap =
    armed &&
    !rangoliOpen &&
    !audioStageOpen &&
    !popup.open &&
    (armed !== "frame" || (!frameAnimating && !frameGone));

  return (
    <div className="dw-game-root">
      <DiwaliHeader />
      <audio
        ref={audioRef}
        src={S3_AUDIO_URL}
        onEnded={handleAudioEnded}
        hidden
      />

      {/* Landscape: the scene image covers the whole screen (see the stage
          below). Portrait: a purpose-made portrait background instead, since
          the scene itself is landscape-composed. */}
      {isPortrait && (
        <img
          src={`${ASSET_BASE}/images/mobile_main.jpg`}
          alt=""
          className="dw-game-bg-portrait"
          onLoad={() => setPortraitBgReady(true)}
        />
      )}

      {/* Landscape stage: the 16:9 scene image, scaled to cover the screen,
          with everything positioned in percentages of it, so it scales as
          one picture. The hidden door sits
          under the frame; the frame is a crop of the same image that falls
          away (same motion as portrait) when its stage is hit. */}
      {!isPortrait && (
        <div className="dw-game-stage">
          <img
            src={`${ASSET_BASE}/images/background-black.jpg`}
            alt=""
            className="dw-game-stage-bg"
            draggable="false"
            onLoad={() => setSceneReady(true)}
          />
          <div className="dw-game-layer dw-game-door" style={LANDSCAPE_FRAME_BOX}>
            <img
              src={`${ASSET_BASE}/images/hidden_door.png`}
              alt=""
              draggable="false"
              style={{
                position: "absolute",
                left: `${((LANDSCAPE_DOOR_IMAGE_PX[0] - FRAME_RECT[0]) / FRAME_RECT[2]) * 100}%`,
                top: `${((LANDSCAPE_DOOR_IMAGE_PX[1] - FRAME_RECT[1]) / FRAME_RECT[3]) * 100}%`,
                width: `${(LANDSCAPE_DOOR_IMAGE_PX[2] / FRAME_RECT[2]) * 100}%`,
              }}
            />
          </div>
          {!frameGone && (
            <div
              className={`dw-game-layer dw-game-frame-layer${frameAnimating ? " dw-frame-animating" : ""}`}
              style={{
                ...LANDSCAPE_FRAME_BOX,
                cursor: canTap && armed === "frame" ? "pointer" : "default",
              }}
              onClick={canTap && armed === "frame" ? () => hitHandlers.frame() : undefined}
            >
              <img
                src={`${ASSET_BASE}/images/background-black.jpg`}
                alt=""
                draggable="false"
                style={LANDSCAPE_FRAME_IMAGE}
              />
            </div>
          )}
          {canTap && armed !== "frame" && armed !== "hiddenDoor" && LANDSCAPE_HOTSPOTS[armed] && (
            <div
              className="dw-game-hotspot"
              style={LANDSCAPE_HOTSPOTS[armed]}
              onClick={() => hitHandlers[armed]()}
            />
          )}
          {canTap && armed === "hiddenDoor" && (
            <div
              className="dw-game-hotspot"
              style={LANDSCAPE_FRAME_BOX}
              onClick={() => hitHandlers.hiddenDoor()}
            />
          )}
        </div>
      )}

      {/* Portrait: mobile_main.jpg has no addressable hotspot elements, so
          the currently-armed stage gets an invisible tap zone positioned
          over its nearest analog in that artwork (PORTRAIT_HOTSPOTS: rug,
          cabinet diya, table sweets) or over the baked-in door once the
          frame's gone (hiddenDoor, same box as the frame below). Hidden
          behind whichever modal that stage opens, so it can't be re-tapped
          mid-puzzle. */}
      {isPortrait && canTap && armed !== "frame" && armed !== "hiddenDoor" && PORTRAIT_HOTSPOTS[armed] && (
        <div
          className="dw-game-portrait-hotspot"
          style={PORTRAIT_HOTSPOTS[armed]}
          onClick={() => hitHandlers[armed]()}
        />
      )}
      {isPortrait && canTap && armed === "hiddenDoor" && (
        <div
          className="dw-game-portrait-hotspot"
          style={PORTRAIT_FRAME_BOX}
          onClick={() => hitHandlers.hiddenDoor()}
        />
      )}

      {/* Portrait: the framed picture, cropped straight out of
          mobile_main.jpg (frame_overlay.png), sitting on top of the same
          spot in the background — visible for the whole game, clickable
          only during its own stage, animates off (dw-frame-animating) then
          unmounts for good (frameGone) revealing the Hidden Door art that's
          already baked into the background underneath. */}
      {isPortrait && !frameGone && (
        <img
          src={`${ASSET_BASE}/images/frame_overlay.png`}
          alt=""
          className={`dw-game-portrait-frame${frameAnimating ? " dw-frame-animating" : ""}`}
          style={{
            ...PORTRAIT_FRAME_BOX,
            cursor: canTap && armed === "frame" ? "pointer" : "default",
          }}
          onClick={canTap && armed === "frame" ? () => hitHandlers.frame() : undefined}
        />
      )}

      {!backgroundReady && <div className="dw-loader" />}

      {popup.open && backgroundReady && (
        <div className="dw-popup-overlay">
          <div className="dw-popup-panel">
            {popup.mode === "cross" && (
              <div className="dw-popup-close" onClick={closeCrossPopup}>
                <img src={`${ASSET_BASE}/images/cross.png`} alt="Close" />
              </div>
            )}
            <img
              src={`${ASSET_BASE}/popup/${popup.image}.png`}
              alt=""
              className="dw-popup-image"
            />
            {popup.mode === "next" && (
              <div className="dw-popup-footer">
                <img
                  src={`${ASSET_BASE}/images/nextbutton.png`}
                  alt="Next"
                  className="dw-next-btn"
                  onClick={closeNextPopup}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {rangoliOpen && (
        <div className="dw-modal-overlay">
          <div className="dw-rangoli-panel">
            <h4 className="dw-rangoli-title">
              Solve the puzzle by clicking on the pieces and complete the
              Rangoli to find the next clue
            </h4>
            <div className="dw-rangoli-stage">
              <img
                src={`${ASSET_BASE}/images/rangoli_complete.png`}
                alt=""
                className="dw-rangoli-guide"
              />
              <img
                src={`${ASSET_BASE}/images/Third_center.png`}
                alt=""
                className="dw-rangoli-piece dw-rangoli-piece-1"
                style={{ transform: `rotate(${RANGOLI_BASE_1 + rot1Extra}deg)` }}
                onClick={() => handleRangoliRotate(1)}
              />
              <img
                src={`${ASSET_BASE}/images/second_middle.png`}
                alt=""
                className="dw-rangoli-piece dw-rangoli-piece-2"
                style={{ transform: `rotate(${RANGOLI_BASE_2 + rot2Extra}deg)` }}
                onClick={() => handleRangoliRotate(2)}
              />
              <img
                src={`${ASSET_BASE}/images/First_outer.png`}
                alt=""
                className="dw-rangoli-piece dw-rangoli-piece-3"
              />
            </div>
            <div className="dw-rangoli-submit" onClick={handleRangoliSubmit}>
              <img src={`${ASSET_BASE}/images/nextbutton.png`} alt="Submit" />
            </div>
          </div>
        </div>
      )}

      {audioStageOpen && (
        <div className="dw-modal-overlay">
          <div className="dw-audio-panel">
            <h4 className="dw-rangoli-title">
              Listen to the conversation to decode the next clue
            </h4>
            <img
              src={`${ASSET_BASE}/images/Play.png`}
              alt="Play"
              className="dw-audio-play"
              style={{
                opacity: audioPlaying ? 0.4 : 1,
                pointerEvents: audioPlaying ? "none" : "auto",
              }}
              onClick={handleAudioButtonClick}
            />
          </div>
        </div>
      )}

    </div>
  );
}
